import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getAuth, applyActionCode } from '../staffAuthApi';
import { app } from '../appAuth';
import './ActionLinkHandler.css';

/**
 * This component handles email action links and redirects to our custom handlers.
 */
const ActionLinkHandler = ({ testMode = null }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const location = useLocation();
  
  const getBaseUrl = () => {
    // In production, use the deployed URL
    if (process.env.NODE_ENV === 'production') {
      return 'https://neatschedule.com';
    }
    // In development, use localhost with the correct port
    return 'http://localhost:3006';
  };

  useEffect(() => {
    // If we're in test mode, simulate different states
    if (testMode) {
      setLoading(false);
      
      switch (testMode) {
        case 'loading':
          setLoading(true);
          break;
        case 'success':
          setMessage("Email verified successfully! Redirecting you...");
          setRedirectUrl("http://localhost:3006/main/verify-email?success=true");
          break;
        case 'error':
          setError("Invalid or expired action code. Please try again.");
          break;
        case 'message':
          setMessage("Please click the button below to complete your action.");
          setRedirectUrl("http://localhost:3006/app/complete-action");
          break;
        default:
          break;
      }
      return;
    }

    const handleRedirect = async () => {
      try {
        console.log('ActionLinkHandler - Current URL:', window.location.href);
        
        // Extract parameters from the URL
        const urlParams = new URLSearchParams(window.location.search);
        const mode = urlParams.get('mode');
        const oobCode = urlParams.get('oobCode');
        const apiKey = urlParams.get('apiKey');
        const continueUrl = urlParams.get('continueUrl');
        
        console.log('ActionLinkHandler - Parameters:', { mode, oobCode, apiKey, continueUrl });
        
        if (!mode || !oobCode) {
          throw new Error('Missing required parameters');
        }
        
        try {
          // For email verification, we need to apply the action code first
          if (mode === 'verifyEmail') {
            const auth = getAuth(app);
            console.log('Applying action code for email verification:', oobCode);
            await applyActionCode(auth, oobCode);
            console.log('Email verification successful');
            
            // Redirect to the email verification page with success parameter
            const redirectPath = '/main/verify-email';
            const redirectUrl = `${getBaseUrl()}${redirectPath}?mode=${mode}&oobCode=${oobCode}&success=true`;
            setRedirectUrl(redirectUrl);
            setMessage("Email verified successfully! Redirecting you...");
            
            // Use a timeout to allow the user to see the message
            setTimeout(() => {
              window.location.href = redirectUrl;
            }, 1500);
          } else if (mode === 'verifyAndChangeEmail') {
            // Handle verify-and-change-email flow (from verifyBeforeUpdateEmail)
            const auth = getAuth(app);
            console.log('Applying action code for verifyAndChangeEmail:', oobCode);
            await applyActionCode(auth, oobCode);
            console.log('Email change + verification successful');

            // Redirect to verification page with success
            const redirectPath = '/main/verify-email';
            const redirectUrl = `${getBaseUrl()}${redirectPath}?mode=${mode}&oobCode=${oobCode}&success=true`;
            setRedirectUrl(redirectUrl);
            setMessage("Your email has been updated and verified. Redirecting you...");

            setTimeout(() => {
              window.location.href = redirectUrl;
            }, 1500);
          } else if (mode === 'resetPassword') {
            // For password reset, redirect to the password reset page
            const redirectPath = '/app/reset-password';
            const redirectUrl = `${getBaseUrl()}${redirectPath}?mode=${mode}&oobCode=${oobCode}`;
            setRedirectUrl(redirectUrl);
            setMessage("Redirecting to password reset page...");
            
            // Use a timeout to allow the user to see the message
            setTimeout(() => {
              window.location.href = redirectUrl;
            }, 1500);
          } else if (mode === 'recoverEmail') {
            // For email recovery, redirect to the email recovery page
            const redirectPath = '/app/recover-email';
            const redirectUrl = `${getBaseUrl()}${redirectPath}?mode=${mode}&oobCode=${oobCode}`;
            setRedirectUrl(redirectUrl);
            setMessage("Redirecting to email recovery page...");
            
            // Use a timeout to allow the user to see the message
            setTimeout(() => {
              window.location.href = redirectUrl;
            }, 1500);
          } else {
            // For other actions, show a button to complete the action
            setMessage("Please click the button below to complete your action.");
            setLoading(false);
          }
        } catch (error) {
          console.error('Error handling action:', error);
          setError(error.message);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error handling redirect:', error);
        setError(error.message);
        setLoading(false);
      }
    };
    
    handleRedirect();
  }, [testMode]);

  const handleManualRedirect = () => {
    if (redirectUrl) {
      if (testMode) {
        // In test mode, just show an alert
        alert('In test mode: Would redirect to ' + redirectUrl);
      } else {
        window.location.href = redirectUrl;
      }
    }
  };

  // Function to handle completing the action manually
  const handleCompleteAction = async () => {
    try {
      setLoading(true);
      
      if (testMode) {
        // In test mode, simulate success
        setTimeout(() => {
          setLoading(false);
          setError(null);
          setMessage("Test action completed successfully!");
        }, 1000);
        return;
      }
      
      // Extract parameters from the URL
      const urlParams = new URLSearchParams(window.location.search);
      const mode = urlParams.get('mode');
      const oobCode = urlParams.get('oobCode');
      
      if (mode === 'verifyEmail') {
        // For email verification, redirect to the email verification page with success parameter
        const redirectPath = '/main/verify-email';
        const redirectUrl = `${getBaseUrl()}${redirectPath}?mode=${mode}&oobCode=${oobCode}&success=true`;
        window.location.href = redirectUrl;
      } else if (mode === 'resetPassword') {
        // For password reset, redirect to the password reset page
        const redirectPath = '/app/reset-password';
        const redirectUrl = `${getBaseUrl()}${redirectPath}?mode=${mode}&oobCode=${oobCode}`;
        window.location.href = redirectUrl;
      } else if (mode === 'recoverEmail') {
        // For email recovery, redirect to the email recovery page
        const redirectPath = '/app/recover-email';
        const redirectUrl = `${getBaseUrl()}${redirectPath}?mode=${mode}&oobCode=${oobCode}`;
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error('Error completing action:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  return (
    <div className="action-link-container" style={{ background: 'transparent' }}>
      <div className="action-link-content" style={{ maxWidth: 560, margin: '0 auto' }}>
        <h1 className="action-link-title" style={{ textAlign: 'center', marginBottom: 16 }}>Account Action</h1>
        {loading && !error && !message && (
          <div className="loading-state" style={{ textAlign: 'center' }}>
            <div className="spinner"></div>
            <p className="loading-text">Processing your request...</p>
          </div>
        )}

        {error && (
          <div className="error-state" style={{ textAlign: 'center' }}>
            <div className="error-alert">
              <svg className="error-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span>{error}</span>
            </div>
            <button 
              className="btn btn-primary"
              onClick={handleCompleteAction}
            >
              Try Again
            </button>
          </div>
        )}

        {message && (
          <div className="message-state" style={{ textAlign: 'center' }}>
            <h2 className="message-title">{message}</h2>
            {!loading && redirectUrl && (
              <button 
                className="btn btn-primary"
                onClick={handleManualRedirect}
              >
                Continue
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionLinkHandler; 